/** Contratos de autenticacion, sesion y sesion offline. */

import { z } from 'zod';
import { idSchema, instantSchema } from './common.js';

export const roleSchema = z.enum(['ADMINISTRADOR', 'CAJA']);

export const loginRequestSchema = z.object({
  username: z.string().trim().min(3).max(64),
  password: z.string().min(8).max(200),
  /**
   * Identificador logico de la instalacion (no del hardware). Se genera en el
   * navegador la primera vez y se guarda localmente: permite revocar un dispositivo
   * sin usar huellas invasivas ni identificadores persistentes del equipo.
   */
  installationId: idSchema,
  installationLabel: z.string().trim().max(80).optional(),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const sessionUserSchema = z.object({
  id: idSchema,
  username: z.string(),
  fullName: z.string(),
  institutionId: idSchema,
  institutionName: z.string(),
  roles: z.array(roleSchema).min(1),
  permissions: z.array(z.string()),
  timeZone: z.string(),
  currency: z.string(),
});
export type SessionUser = z.infer<typeof sessionUserSchema>;

export const loginResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  accessTokenExpiresAt: instantSchema,
  refreshTokenExpiresAt: instantSchema,
  /**
   * Hasta cuando la PWA puede seguir operando sin conexion con esta sesion.
   * Pasado ese instante debe volver a validarse contra el servidor.
   */
  offlineValidUntil: instantSchema,
  user: sessionUserSchema,
});
export type LoginResponse = z.infer<typeof loginResponseSchema>;

export const refreshRequestSchema = z.object({
  refreshToken: z.string().min(10),
  installationId: idSchema,
});

export const logoutRequestSchema = z.object({
  refreshToken: z.string().min(10).optional(),
  /** `true` cierra la sesion en todas las instalaciones del usuario. */
  allDevices: z.boolean().default(false),
});

/**
 * Flujo de restablecimiento de acceso. Se implementa de referencia: la API genera
 * un token de un solo uso con vigencia corta y lo devuelve SOLO en modo desarrollo.
 * En produccion se entrega por un canal externo (correo), que es un adaptador
 * sustituible y opcional para no obligar a contratar un servicio de correo.
 */
export const passwordResetRequestSchema = z.object({
  username: z.string().trim().min(3).max(64),
});

export const passwordResetConfirmSchema = z.object({
  token: z.string().min(20).max(200),
  newPassword: z.string().min(10).max(200),
});

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(8).max(200),
  newPassword: z.string().min(10).max(200),
});

/**
 * Revalidacion al recuperar conexion: el servidor confirma que el usuario, el rol y
 * el dispositivo siguen autorizados ANTES de aceptar cualquier operacion offline.
 */
export const revalidateResponseSchema = z.object({
  valid: z.boolean(),
  user: sessionUserSchema.nullable(),
  offlineValidUntil: instantSchema.nullable(),
  revokedReason: z
    .enum(['USUARIO_REVOCADO', 'DISPOSITIVO_REVOCADO', 'SESION_EXPIRADA', 'ROL_CAMBIADO'])
    .nullable(),
});
export type RevalidateResponse = z.infer<typeof revalidateResponseSchema>;

export const installationSchema = z.object({
  id: idSchema,
  label: z.string(),
  userId: idSchema,
  userName: z.string(),
  registeredAt: instantSchema,
  lastSeenAt: instantSchema.nullable(),
  revokedAt: instantSchema.nullable(),
});
export type InstallationDto = z.infer<typeof installationSchema>;
